import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { z } from 'zod';
import { parseRoles, type Role } from '@unit/shared';
import { firestore } from '../../lib/firebase.js';

/**
 * The shape the home screen needs from the caller's own `users` document.
 * Read directly through the Firestore SDK on purpose: this is the read that
 * proves the security rules allow a soldier their own document and nothing
 * else. Every *write* still goes through a Cloud Function.
 */
const profileDocSchema = z.object({
  firstName: z.string().default(''),
  lastName: z.string().default(''),
  rank: z.string().nullish(),
  title: z.string().nullish(),
  personalNumber: z.string().default(''),
  teamId: z.string().nullish(),
  platoonId: z.string().nullish(),
  status: z.enum(['active', 'inactive', 'released']).catch('active'),
});

export interface MyProfile {
  firstName: string;
  lastName: string;
  rank: string | null;
  title: string | null;
  personalNumber: string;
  teamId: string | null;
  platoonId: string | null;
  status: 'active' | 'inactive' | 'released';
  roles: Role[];
}

type State =
  | { kind: 'loading' }
  | { kind: 'ready'; profile: MyProfile }
  | { kind: 'error'; message: string };

export function useMyProfile(uid: string | null, claimRoles: Role[]): State {
  const [state, setState] = useState<State>({ kind: 'loading' });

  useEffect(() => {
    if (!uid) {
      setState({ kind: 'loading' });
      return;
    }
    setState({ kind: 'loading' });

    return onSnapshot(
      doc(firestore, 'users', uid),
      (snap) => {
        if (!snap.exists()) {
          setState({ kind: 'error', message: `No user document for ${uid}` });
          return;
        }
        const raw = snap.data();
        const parsed = profileDocSchema.safeParse(raw);
        if (!parsed.success) {
          setState({ kind: 'error', message: parsed.error.message });
          return;
        }
        setState({
          kind: 'ready',
          profile: {
            ...parsed.data,
            rank: parsed.data.rank ?? null,
            title: parsed.data.title ?? null,
            teamId: parsed.data.teamId ?? null,
            platoonId: parsed.data.platoonId ?? null,
            // Claims are the enforcement source of truth; the mirrored field on
            // the document is only a fallback for display before claims land.
            roles: claimRoles.length > 0 ? claimRoles : parseRoles(raw.roles),
          },
        });
      },
      (error) => setState({ kind: 'error', message: error.message }),
    );
  }, [uid, claimRoles]);

  return state;
}
